import { ElementType, ReactNode } from 'react';
import { Card, CardContent } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { cn } from '../../../lib/utils';

export interface AdminMetricCardProps {
  title: string;
  value: string | number | null | undefined;
  subLabel?: ReactNode;
  icon: ElementType;
  trend?: {
    value: string;
    positive?: boolean;
    neutral?: boolean;
  };
  badge?: {
    label: string;
    variant?: 'success' | 'warning' | 'destructive' | 'info' | 'secondary' | 'outline';
  };
  isLoading?: boolean;
  onClick?: () => void;
  className?: string;
}

export function AdminMetricCard({
  title,
  value,
  subLabel,
  icon: Icon,
  trend,
  badge,
  isLoading = false,
  onClick,
  className,
}: AdminMetricCardProps) {
  return (
    <Card
      onClick={onClick}
      className={cn(
        'relative overflow-hidden bg-card border-border shadow-sm transition-all',
        onClick && 'cursor-pointer hover:border-primary/50 hover:shadow-md',
        className
      )}
    >
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between text-muted-foreground">
          <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground/90">
            {title}
          </span>
          <div className="size-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Icon className="size-4" />
          </div>
        </div>

        <div className="flex items-baseline justify-between gap-2">
          {isLoading ? (
            <div className="h-8 w-20 bg-muted/60 animate-pulse rounded" />
          ) : (
            <span className="text-2xl font-bold font-display text-foreground tracking-tight">
              {value !== null && value !== undefined && value !== '' ? value : '—'}
            </span>
          )}

          {badge && (
            <Badge
              variant={badge.variant === 'secondary' ? 'secondary' : 'outline'}
              className={cn(
                'text-[10px] font-medium shrink-0',
                badge.variant === 'success' && 'bg-success/10 text-success border-success/30',
                badge.variant === 'warning' && 'bg-warning/10 text-warning border-warning/30',
                badge.variant === 'destructive' && 'bg-destructive/10 text-destructive border-destructive/30',
                badge.variant === 'info' && 'bg-info/10 text-info border-info/30'
              )}
            >
              {badge.label}
            </Badge>
          )}

          {trend && (
            <span
              className={cn(
                'text-xs font-medium shrink-0',
                trend.positive && 'text-success',
                trend.positive === false && 'text-destructive',
                trend.neutral && 'text-muted-foreground'
              )}
            >
              {trend.value}
            </span>
          )}
        </div>

        {subLabel && (
          <div className="text-[11px] text-muted-foreground leading-tight truncate">
            {subLabel}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
